import {
  getNodeInfoForType,
  isProductionReadyNodeType,
  type NodeType,
} from './nodes/registry';
import './LogiBlocks.scss';

interface TriggerEntry {
  nodeType: NodeType;
  /** Optional override for the label shown in this selector. Falls back to NodeInfo.label. */
  displayLabel?: string;
  description: string;
}

interface TriggerSection {
  title: string;
  triggers: TriggerEntry[];
}

const TRIGGER_SECTIONS: TriggerSection[] = [
  {
    title: 'Shipment events',
    triggers: [
      {
        nodeType: 'alert_trigger',
        displayLabel: 'Alert',
        description: 'An alert is triggered',
      },
      {
        nodeType: 'alert_action_taken_trigger',
        displayLabel: 'Alert action taken',
        description: 'Resolved, dismissed',
      },
      {
        nodeType: 'status_progress_trigger',
        displayLabel: 'Status progress',
        description: 'The shipment status updates',
      },
      {
        nodeType: 'eta_change_trigger',
        description: 'The ETA shifts past a threshold',
      },
      {
        nodeType: 'location_trigger',
        description: 'Shipment reaches a location',
      },
    ],
  },
  {
    title: 'External & Ad hoc',
    triggers: [
      {
        nodeType: 'inbound_trigger',
        description: 'Call, Email, or SMS received',
      },
      {
        nodeType: 'schedule_trigger_v2',
        description: 'Runs on a recurring schedule (daily, ...)',
      },
      {
        nodeType: 'manual_execution_trigger',
        description: 'An operator runs it on demand',
      },
    ],
  },
];

interface InitialTriggerSelectorProps {
  onSelectTrigger: (nodeType: NodeType) => void;
}

/**
 * UI shown when canvas has no nodes.
 * Allows user to initialize the workflow with a trigger node.
 */
export function InitialTriggerSelector({
  onSelectTrigger,
}: InitialTriggerSelectorProps) {
  return (
    <div className='initial-trigger-selector'>
      <div className='initial-trigger-selector__header'>
        <div className='initial-trigger-selector__title'>Choose a trigger</div>
        <div className='initial-trigger-selector__subtitle'>
          The event that starts this automation
        </div>
      </div>
      {TRIGGER_SECTIONS.map((section) => (
        <div key={section.title} className='initial-trigger-selector__section'>
          <div className='initial-trigger-selector__section-title'>
            {section.title}
          </div>
          {section.triggers.map((trigger) => {
            const info = getNodeInfoForType(trigger.nodeType);
            const isReady = isProductionReadyNodeType(trigger.nodeType);
            const label = trigger.displayLabel ?? info.label;
            return (
              <button
                key={trigger.nodeType}
                type='button'
                className='initial-trigger-selector__row'
                aria-disabled={!isReady}
                title={isReady ? undefined : 'Not available in production yet'}
                onClick={
                  isReady ? () => onSelectTrigger(trigger.nodeType) : undefined
                }
              >
                <div className='initial-trigger-selector__row-icon'>
                  {info.getIcon(16)}
                </div>
                <div className='initial-trigger-selector__row-text'>
                  <div className='initial-trigger-selector__row-label'>
                    {label}
                  </div>
                  <div className='initial-trigger-selector__row-description'>
                    {trigger.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
